import React from 'react';
import { useSelector } from 'react-redux';
import BulkUploadListFilters from '../components/BulkUploadListFilters';
import TopBar from '../../../components/TopBar';
import BulkUploadTable from '../components/BulkUploadTable';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { Stack } from '@mui/material';
import FeatureNotSupportedDialog from '../../common/FeatureNotSupportedDialog';
import useBulkUploadFilters from '../../../components/filters/BulkUploadFilterHelper';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import useSessionPersistedState from '../../../hooks/useSessionPersistedState';

const PARTY_PROFILE_CATEGORIES = new Set(['CUSTOMER', 'SUPPLIER', 'PRODUCT']);

function BulkUploadListPage({ category = 'E_INVOICE', subCategory = null }) {
  const isSalesLedger = category === 'E_INVOICE' && subCategory === 'SALES';
  const isPurchasesLedger = category === 'E_INVOICE' && subCategory === 'PURCHASES';
  const supportsCategoryFilter = isSalesLedger || isPurchasesLedger;
  const categoryFilterSessionKey = isPurchasesLedger
    ? 'purchases/bulk-upload:selectedCategory'
    : isSalesLedger
      ? 'sales/bulk-upload:selectedCategory'
      : undefined;
  const categoryFilterOptions = isPurchasesLedger ? ['E_INVOICE', 'SUPPLIER'] : ['E_INVOICE', 'CUSTOMER', 'PRODUCT'];

  const [selectedCategory, setSelectedCategory] = useSessionPersistedState(categoryFilterSessionKey, category);

  const effectiveCategory = supportsCategoryFilter ? selectedCategory : category;
  const effectiveSubCategory = PARTY_PROFILE_CATEGORIES.has(effectiveCategory) ? null : subCategory;

  const { bulkUpload } = useTranslatedMessages();
  const placeholders = bulkUpload?.filters?.search_placeholders ?? {};
  const page = bulkUpload?.page ?? {};

  useDocumentTitle(page?.document_title ?? 'Bulk Upload List');
  const subscription = useSelector((state) => state.subscription.subscription);
  const user = useSelector((/** @type {any} */ state) => state.user.user);
  const isOrganizationOwner = Array.isArray(user?.roles) && user.roles.includes('ORGANIZATION_OWNER');
  const isBulkUploadFeatureEnabled = subscription?.features?.enable_bulk_upload;
  const isSftpFeatureEnabled = subscription?.features?.enable_sftp;

  const persistKey =
    effectiveCategory === 'CUSTOMER'
      ? 'sales/bulk-upload-customer'
      : effectiveCategory === 'PRODUCT'
        ? 'sales/bulk-upload-product'
        : effectiveCategory === 'SUPPLIER'
          ? 'purchases/bulk-upload-supplier'
          : effectiveCategory === 'NON_E_INVOICE'
            ? 'register/bulk-upload'
            : effectiveSubCategory === 'PURCHASES'
              ? 'purchases/bulk-upload'
              : 'sales/bulk-upload';

  const {
    searchTerm,
    selectedFilter,
    selectedStatus,
    currentQuery,
    handleFilterChange,
    handleSearchChange,
    handleSearchKeyPress,
    handleDateRangeChange,
    handlePaginationChange,
    handleStatusChange,
  } = useBulkUploadFilters({ persistKey });

  const filters = [
    {
      type: 'search',
      options: [
        {
          key: 'filename',
          placeholder: placeholders.filename ?? 'File Name',
        },
        {
          key: 'status',
          placeholder: placeholders.status ?? 'Status',
        },
      ],
    },
  ];

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  return isBulkUploadFeatureEnabled || isSftpFeatureEnabled ? (
    <Stack direction="column" sx={{ mt: '-1rem' }}>
      <TopBar title={page?.top_bar_title ?? 'Bulk Upload'}></TopBar>
      <BulkUploadListFilters
        filters={filters}
        selectedFilter={selectedFilter}
        selectedStatus={selectedStatus}
        onFilterChange={handleFilterChange}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        onSearchKeyPress={handleSearchKeyPress}
        onDateRangeChange={handleDateRangeChange}
        onStatusChange={handleStatusChange}
        showCategoryFilter={supportsCategoryFilter}
        selectedCategory={selectedCategory}
        onCategoryChange={handleCategoryChange}
        categoryFilterOptions={categoryFilterOptions}
      />
      <BulkUploadTable
        handlePaginationChange={handlePaginationChange}
        searchQuery={{ ...currentQuery, category: effectiveCategory, subCategory: effectiveSubCategory }}
        showCreatedBy={isOrganizationOwner}
      />
    </Stack>
  ) : (
    !isBulkUploadFeatureEnabled && !isSftpFeatureEnabled && (
      <FeatureNotSupportedDialog
        title={page?.feature_not_supported?.title ?? 'Bulk Upload'}
        description={
          page?.feature_not_supported?.description ??
          'Upload and process multiple documents simultaneously, saving time and streamlining your workflow.'
        }
      />
    )
  );
}

export default BulkUploadListPage;
